// ============================================
// API Service - Handles all API calls
// ============================================

import axios from 'axios';

// API URL - determined at runtime based on current hostname
const getApiUrl = () => {
  // Check if running on localhost
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3001/api';
    }
  }
  // Production - use HTTPS API domain
  return 'https://api.steaming-th.com/api';
};

// Create a getter that evaluates at runtime
let _apiUrl = null;
const getApiUrlCached = () => {
  if (_apiUrl === null) {
    _apiUrl = getApiUrl();
    console.log('API URL:', _apiUrl);
  }
  return _apiUrl;
};

// Create axios instance with lazy URL evaluation
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Set baseURL on first request
axiosInstance.interceptors.request.use((config) => {
  if (!config.baseURL) {
    config.baseURL = getApiUrlCached();
  }
  return config;
});

// API object with all methods
const api = {
  // Set auth token
  setAuthToken: (token) => {
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  },

  // ============================================
  // Auth APIs
  // ============================================
  
  login: async (username, password) => {
    const response = await axiosInstance.post('/auth/login', {
      username,
      password
    });
    return response.data;
  },

  register: async (username, password) => {
    const response = await axiosInstance.post('/auth/register', {
      username,
      password
    });
    return response.data;
  },

  // ============================================
  // User APIs
  // ============================================

  getProfile: async () => {
    const response = await axiosInstance.get('/user/profile');
    return response.data;
  },

  updateProfileImage: async (imageUrl) => {
    const response = await axiosInstance.post('/user/profile-image', {
      imageUrl
    });
    return response.data;
  },

  // ============================================
  // Assets APIs
  // ============================================

  getAssets: async () => {
    const response = await axiosInstance.get('/assets');
    return response.data;
  },

  getAsset: async (id) => {
    const response = await axiosInstance.get(`/assets/${id}`);
    return response.data;
  },

  // ============================================
  // Trading APIs
  // ============================================

  placeTrade: async (assetId, amount, type, duration) => {
    const response = await axiosInstance.post('/trade/place', {
      assetId,
      amount,
      type,
      duration
    });
    return response.data;
  },

  getTradeHistory: async () => {
    const response = await axiosInstance.get('/trade/history');
    return response.data;
  },

  // ============================================
  // Transaction APIs
  // ============================================

  requestDeposit: async (amount) => {
    const response = await axiosInstance.post('/transaction/deposit', {
      amount
    });
    return response.data;
  },

  requestWithdraw: async (amount, currency, network, address, firstName, lastName, bankName) => {
    const response = await axiosInstance.post('/transaction/withdraw', {
      amount,
      currency,
      network,
      address,
      firstName,
      lastName,
      bankName
    });
    return response.data;
  },

  transfer: async (recipientId, amount) => {
    const response = await axiosInstance.post('/transaction/transfer', {
      recipientId,
      amount
    });
    return response.data;
  },

  getTransactionHistory: async () => {
    const response = await axiosInstance.get('/transaction/history');
    return response.data;
  },

  // ============================================
  // Loan APIs
  // ============================================

  uploadLoanDocument: async (formData) => {
    const response = await axiosInstance.post('/transaction/loan/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  requestLoan: async (loanData) => {
    const response = await axiosInstance.post('/transaction/loan', loanData);
    return response.data;
  },

  getLoanHistory: async () => {
    const response = await axiosInstance.get('/transaction/loans');
    return response.data;
  },

  // ============================================
  // Verification APIs
  // ============================================

  submitVerification: async (verificationData) => {
    const formData = new FormData();
    formData.append('fullName', verificationData.fullName);
    formData.append('idCardNumber', verificationData.idCardNumber);
    
    if (!verificationData.idCardFront || !verificationData.idCardFront.file) {
      throw new Error('กรุณาอัพโหลดรูปบัตรประชาชนด้านหน้า');
    }
    if (!verificationData.idCardBack || !verificationData.idCardBack.file) {
      throw new Error('กรุณาอัพโหลดรูปบัตรประชาชนด้านหลัง');
    }
    if (!verificationData.selfieWithIdCard || !verificationData.selfieWithIdCard.file) {
      throw new Error('กรุณาอัพโหลดรูปถ่ายคู่บัตรประชาชน');
    }
    
    formData.append('idCardFront', verificationData.idCardFront.file);
    formData.append('idCardBack', verificationData.idCardBack.file);
    formData.append('selfieWithIdCard', verificationData.selfieWithIdCard.file);
    
    if (verificationData.otherDocuments && verificationData.otherDocuments.length > 0) {
      verificationData.otherDocuments.forEach((doc) => {
        if (doc.file) {
          formData.append('otherDocuments', doc.file);
        }
      });
    }

    const response = await axiosInstance.post('/user/verification/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Handle specific status codes
      if (error.response.status === 429) {
        // Suppress console error for 429, just show user-friendly message
        const customError = new Error('คำขอยังมากเกินไป กรุณาลองอีกครั้งในภายหลัง');
        customError.suppressConsole = true;
        customError.is429 = true;
        throw customError;
      }
      
      // Server responded with error
      const message = error.response.data?.error || error.response.data?.message || 'เกิดข้อผิดพลาด';
      throw new Error(message);
    } else if (error.request) {
      // Request made but no response
      throw new Error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต');
    } else {
      // Something else happened
      throw new Error('เกิดข้อผิดพลาดที่ไม่คาดคิด');
    }
  }
);

export default api;
